// tier-policy.js — single source of truth for tier-aware behavior.
//
// PR A of the Free-tier gating arc (Free / Team / Enterprise unit
// economics work). Free users get longer poll intervals and no SSE
// to keep per-user infra cost near $0; Team + Enterprise users get
// the full 15-30s polling cadence + live SSE that PR #134's scaling
// pass shipped.
//
// Tier values come from `/v1/teams/info` → `teamState.info.tier`.
// DB-side values are `'solo' / 'startup' / 'enterprise'` (per
// migration 003); public-facing names per PR #136 are Free / Team /
// Enterprise. This module operates on the DB-side identifier — the
// rename was display-only.
//
// Why dynamic intervals instead of setInterval-at-boot:
//   - teamState.info isn't populated synchronously at boot (it lands
//     when fetchAll's /v1/teams/info resolves, ~50-500ms after page
//     load depending on cold-cache state).
//   - Users can upgrade mid-session (Stripe checkout → webhook fires
//     → /v1/billing/status flips → next teamInfo refresh has new
//     tier). The dashboard should pick up the new cadence on the
//     next poll cycle without a page reload.
//   - Dynamic intervals via recursive setTimeout let each tick read
//     the current tier and schedule the next tick at the right
//     interval. ~1 LOC more than setInterval, infinitely more
//     correct.

import { teamState } from "./page-state.js";

// Tier intervals in milliseconds, per cost class. See
// docs/cost-model.md for the unit-economics math.
//
// Free tier rationale (~$0.03/user/mo target):
//   - leasesPoll 60s: snapshot endpoints (sync/state, members,
//     heartbeats). 60s is fine for solo users — they're the only
//     ones generating activity for their tenant, so the dashboard's
//     "fresh state" mostly reflects what their own daemon is doing,
//     which they can already see locally at 127.0.0.1:7777.
//   - countsPoll 60s: hero tile aggregates. Same logic.
//   - SSE disabled: poll covers the event volume for a solo user.
//     Skipping the persistent DO connection saves ~$0.10/user/mo.
//   - auditPoll 10min: rare admin actions, can stale longer.
//
// Team tier rationale (~$0.55/user/mo target):
//   - leasesPoll 15s: matches PR #134's stretched value.
//   - countsPoll 30s.
//   - SSE enabled: live coordination events delivered sub-second
//     via the existing TenantEventStream DO.
//   - auditPoll 5min.
//
// Enterprise tier rationale: same as Team. Different price point,
// not a different rate. The differentiation is in features (SSO,
// audit retention, more active machines per user), not poll cadence.
//
// Cost class hierarchy: leasesPoll runs the 3-endpoint refreshLeases
// Promise.all, so its interval governs ~3 req/cycle. countsPoll +
// leasesPoll-on-overview each fire 1 req/cycle. auditPoll = 1 req.
const TIER_INTERVALS = {
  solo: {
    leasesPoll: 60_000,      // /v1/sync/state, /v1/teams/members, /v1/teams/heartbeats
    countsPoll: 60_000,      // /v1/events/counts (daemon mode only)
    overviewCounts: 60_000,  // /v1/teams/events/decision-counts (cloud mode)
    overviewLeases: 60_000,  // /v1/teams/leases (cloud mode)
    auditPoll: 10 * 60_000,  // /v1/teams/audit
    sseEnabled: false,       // poll-only on Free
  },
  startup: {
    leasesPoll: 15_000,
    countsPoll: 30_000,
    overviewCounts: 30_000,
    overviewLeases: 30_000,
    auditPoll: 5 * 60_000,
    sseEnabled: true,
  },
  enterprise: {
    leasesPoll: 15_000,
    countsPoll: 30_000,
    overviewCounts: 30_000,
    overviewLeases: 30_000,
    auditPoll: 5 * 60_000,
    sseEnabled: true,
  },
};

// Pre-tier-known default. Until teamState.info loads we don't know
// the user's tier — assume Free (the cautious default that
// minimizes cost). The first tick after teamState.info lands will
// flip to the right cadence. Worst case: a Team user sees one
// 60s poll cycle before the cadence accelerates; functionally
// invisible.
const UNKNOWN_TIER_INTERVALS = TIER_INTERVALS.solo;

// getActiveTier returns the DB-side tier identifier from
// teamState.info, or null if not loaded yet.
export function getActiveTier() {
  return (teamState.info && teamState.info.tier) || null;
}

// getTierIntervals returns the interval object for the given tier
// (or the active tier if no arg). Falls back to Free defaults for
// unknown values — defensive against future schema changes that
// add a new tier name the dashboard doesn't know yet.
export function getTierIntervals(tier) {
  const t = tier || getActiveTier();
  if (t && Object.prototype.hasOwnProperty.call(TIER_INTERVALS, t)) {
    return TIER_INTERVALS[t];
  }
  return UNKNOWN_TIER_INTERVALS;
}

// isSSEEnabled returns true when the active tier should open a live
// SSE stream. Free tier returns false — the poll covers event
// volume, and the DO connection cost is the single biggest
// per-user line in the cost model.
export function isSSEEnabled() {
  return getTierIntervals().sseEnabled;
}

// scheduleDynamic — wraps a recurring task in a self-rescheduling
// setTimeout that re-reads the tier-specific interval each cycle.
// This is the load-bearing primitive of tier-aware polling.
//
// Usage:
//   scheduleDynamic('leasesPoll', refreshLeases);
//
// The 'key' is a property name from a tier interval object. The
// function runs, then schedules its next invocation using
// getTierIntervals()[key]. If the tier changes mid-session, the
// next tick picks up the new cadence automatically. No teardown
// needed when the tier flips — the next setTimeout just uses
// whatever's current.
//
// Returns a cancel function the caller can invoke for cleanup.
export function scheduleDynamic(key, fn) {
  let cancelled = false;
  let timer = null;
  // resolveNext returns the configured interval for `key`, or a
  // safe fallback if the key isn't in the tier's interval map.
  // Without this guard, a typo or future tier-key add that doesn't
  // populate every tier's TIER_INTERVALS row could return undefined,
  // and setTimeout(tick, undefined) coerces to setTimeout(tick, 0),
  // creating a busy-loop that pegs the CPU. The 60s fallback matches
  // the Free-tier default — slow enough to be obviously throttled,
  // not aggressive enough to harm.
  function resolveNext() {
    const intervals = getTierIntervals();
    const v = intervals && intervals[key];
    if (typeof v === "number" && v > 0 && Number.isFinite(v)) {
      return v;
    }
    console.error(
      "[tier-policy] no valid interval for key '" + key + "'; falling back to 60s. " +
      "This means TIER_INTERVALS is missing an entry — fix tier-policy.js."
    );
    return 60_000;
  }
  function tick() {
    if (cancelled) return;
    try {
      // Don't await — fn may be async, but scheduling the next tick
      // shouldn't wait for it. The 60s cadence is safe even with
      // overlapping fn invocations (snapshot endpoints are
      // idempotent; the worst case is one extra request in flight).
      fn();
    } catch (e) {
      // Don't let a single tick crash the loop. Logged for ops.
      console.error("[tier-policy] tick error in " + key + ":", e);
    }
    timer = setTimeout(tick, resolveNext());
  }
  timer = setTimeout(tick, resolveNext());
  return function cancel() {
    cancelled = true;
    if (timer) clearTimeout(timer);
  };
}
